import React from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

const FormField = ({ field, value, onChange, colors, themeColors }) => {
  // Use provided colors or fallback defaults
  const actualColors = colors || {
    primary: '#5489B6',
    secondary: '#002949',
    accent: '#5B5E5A',
    neutral: '#666666',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
  };

  const actualThemeColors = themeColors || {
    bgPrimary: '#ffffff',
    bgSecondary: '#f9fafb',
    bgTertiary: '#e5e7eb',
    textPrimary: '#111827',
    textSecondary: '#6b7280',
    textMuted: '#9ca3af',
    border: '#e5e7eb',
    shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  };

  const getFieldValue = (fieldData, parentId) => {
    if (!parentId) {
      // For top-level fields, return the direct value
      return value || '';
    }
    
    // For nested fields, traverse the object structure
    let currentValue = value;
    if (!currentValue) return '';
    
    if (fieldData.type === 'group') {
      return currentValue;
    }
    
    return currentValue[fieldData.id] || '';
  };

  const updateFieldValue = (newValue, fieldData, parentId) => {
    if (!parentId) {
      // For top-level fields, pass the value directly
      onChange(newValue);
      return;
    }

    // For nested fields in a group
    const updatedValue = { ...value };
    updatedValue[fieldData.id] = newValue;
    onChange(updatedValue);
  };

  const handleChange = (e) => {
    const newValue = e.target.type === 'checkbox' 
      ? handleCheckboxChange(e.target.value, value)
      : e.target.value;
    onChange(newValue);
  };

  const handleCheckboxChange = (checkboxValue, currentValue) => {
    const values = Array.isArray(currentValue) ? currentValue : [];
    if (values.includes(checkboxValue)) {
      return values.filter(v => v !== checkboxValue);
    } else {
      return [...values, checkboxValue];
    }
  };

  const handleAddDynamicItem = () => {
    const currentItems = Array.isArray(value) ? value : [];
    onChange([...currentItems, {}]);
  };

  const handleRemoveDynamicItem = (index) => {
    const currentItems = Array.isArray(value) ? value : [];
    onChange(currentItems.filter((_, i) => i !== index));
  };

  const handleDynamicItemChange = (index, newValue) => {
    const currentItems = Array.isArray(value) ? value : [];
    const updatedItems = [...currentItems];
    updatedItems[index] = newValue;
    onChange(updatedItems);
  };

  const renderField = (fieldData, parentId = '') => {
    const fieldValue = getFieldValue(fieldData, parentId);

    switch (fieldData.type) {
      case 'dynamic':
        const items = Array.isArray(value) ? value : [];
        const minItems = fieldData.minItems || 0;
        
        return (
          <div className="space-y-4">
            {items.map((item, index) => (
              <div 
                key={index} 
                className="relative p-4 rounded-lg transition-colors duration-300"
                style={{ 
                  backgroundColor: actualThemeColors.bgSecondary,
                  border: `1px solid ${actualThemeColors.border}`
                }}
              >
                <div className="absolute top-2 right-2">
                  {items.length > minItems && (
                    <button
                      type="button"
                      onClick={() => handleRemoveDynamicItem(index)}
                      className="transition-colors duration-300"
                      style={{ color: actualColors.error }}
                      onMouseEnter={(e) => {
                        e.target.style.opacity = '0.7';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.opacity = '1';
                      }}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
                <FormField
                  field={fieldData.template}
                  value={item}
                  onChange={(newValue) => handleDynamicItemChange(index, newValue)}
                  colors={actualColors}
                  themeColors={actualThemeColors}
                />
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddDynamicItem}
              className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white transition-colors duration-300"
              style={{ 
                backgroundColor: actualColors.primary,
                boxShadow: actualThemeColors.shadow
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '1';
              }}
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {fieldData.buttonText || 'Add Item'}
            </button>
          </div>
        );

      case 'group':
        return (
          <div key={fieldData.id} className="space-y-4">
            <h3 
              className="text-lg font-medium transition-colors duration-300"
              style={{ color: actualThemeColors.textPrimary }}
            >
              {fieldData.label}
            </h3>
            <div className="space-y-4">
              {fieldData.fields.map(subField => (
                <div key={subField.id}>
                  <FormField
                    field={subField}
                    value={fieldValue?.[subField.id]}
                    onChange={(newValue) => {
                      const updatedGroupValue = {
                        ...fieldValue,
                        [subField.id]: newValue
                      };
                      onChange(updatedGroupValue);
                    }}
                    colors={actualColors}
                    themeColors={actualThemeColors}
                  />
                </div>
              ))}
            </div>
          </div>
        );

      case 'text':
      case 'email':
      case 'tel':
      case 'url':
        return (
          <input
            type={fieldData.type}
            id={fieldData.id}
            name={fieldData.id}
            value={fieldValue}
            onChange={handleChange}
            placeholder={fieldData.placeholder}
            required={fieldData.required}
            className="mt-1 block w-full rounded-md shadow-sm sm:text-sm transition-colors duration-300"
            style={{
              border: `1px solid ${actualThemeColors.border}`,
              backgroundColor: actualThemeColors.bgPrimary,
              color: actualThemeColors.textPrimary,
              boxShadow: actualThemeColors.shadow
            }}
            onFocus={(e) => {
              e.target.style.borderColor = actualColors.primary;
              e.target.style.outline = `2px solid ${actualColors.primary}20`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = actualThemeColors.border;
              e.target.style.outline = 'none';
            }}
          />
        );

      case 'textarea':
        return (
          <textarea
            id={fieldData.id}
            name={fieldData.id}
            value={fieldValue}
            onChange={handleChange}
            placeholder={fieldData.placeholder}
            required={fieldData.required}
            rows={4}
            className="mt-1 block w-full rounded-md shadow-sm sm:text-sm transition-colors duration-300"
            style={{
              border: `1px solid ${actualThemeColors.border}`,
              backgroundColor: actualThemeColors.bgPrimary,
              color: actualThemeColors.textPrimary,
              boxShadow: actualThemeColors.shadow
            }}
            onFocus={(e) => {
              e.target.style.borderColor = actualColors.primary;
              e.target.style.outline = `2px solid ${actualColors.primary}20`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = actualThemeColors.border;
              e.target.style.outline = 'none';
            }}
          />
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            <div 
              className="text-sm font-medium mb-2 transition-colors duration-300"
              style={{ color: actualThemeColors.textPrimary }}
            >
              {fieldData.label}
              {fieldData.required && <span style={{ color: actualColors.error }}>*</span>}
            </div>
            {fieldData.options.map((option) => (
              <div key={option.value} className="flex items-center">
                <input
                  type="checkbox"
                  id={`${fieldData.id}-${option.value}`}
                  value={option.value}
                  checked={Array.isArray(value) && value.includes(option.value)}
                  onChange={handleChange}
                  className="h-4 w-4 rounded transition-colors duration-300"
                  style={{
                    accentColor: actualColors.primary,
                    border: `1px solid ${actualThemeColors.border}`
                  }}
                />
                <label
                  htmlFor={`${fieldData.id}-${option.value}`}
                  className="ml-2 block text-sm transition-colors duration-300"
                  style={{ color: actualThemeColors.textSecondary }}
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            <div 
              className="text-sm font-medium mb-2 transition-colors duration-300"
              style={{ color: actualThemeColors.textPrimary }}
            >
              {fieldData.label}
              {fieldData.required && <span style={{ color: actualColors.error }}>*</span>}
            </div>
            {fieldData.options.map((option) => (
              <div key={option.value} className="flex items-center">
                <input
                  type="radio"
                  id={`${fieldData.id}-${option.value}`}
                  name={fieldData.id}
                  value={option.value}
                  checked={value === option.value}
                  onChange={handleChange}
                  className="h-4 w-4 transition-colors duration-300"
                  style={{
                    accentColor: actualColors.primary,
                    border: `1px solid ${actualThemeColors.border}`
                  }}
                />
                <label
                  htmlFor={`${fieldData.id}-${option.value}`}
                  className="ml-2 block text-sm transition-colors duration-300"
                  style={{ color: actualThemeColors.textSecondary }}
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            id={fieldData.id}
            value={fieldValue}
            onChange={handleChange}
            className="shadow-sm block w-full sm:text-sm rounded-md transition-colors duration-300"
            style={{
              border: `1px solid ${actualThemeColors.border}`,
              backgroundColor: actualThemeColors.bgPrimary,
              color: actualThemeColors.textPrimary,
              boxShadow: actualThemeColors.shadow
            }}
            onFocus={(e) => {
              e.target.style.borderColor = actualColors.primary;
              e.target.style.outline = `2px solid ${actualColors.primary}20`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = actualThemeColors.border;
              e.target.style.outline = 'none';
            }}
          />
        );

      default:
        return null;
    }
  };

  // Check if field should be shown based on conditional logic
  if (field.conditional) {
    const { field: conditionalField, value: conditionalValue, values } = field.conditional;
    const fieldValue = value?.[conditionalField];
    
    if (values) {
      if (!values.includes(fieldValue)) return null;
    } else if (fieldValue !== conditionalValue) {
      return null;
    }
  }

  return (
    <div className="space-y-1">
      {field.type !== 'group' && field.type !== 'checkbox' && field.type !== 'radio' && field.type !== 'dynamic' && (
        <label 
          htmlFor={field.id} 
          className="block text-sm font-medium transition-colors duration-300"
          style={{ color: actualThemeColors.textPrimary }}
        >
          {field.label}
          {field.required && <span style={{ color: actualColors.error }}>*</span>}
        </label>
      )}
      {field.helpText && (
        <p 
          className="text-sm transition-colors duration-300"
          style={{ color: actualColors.primary }}
        >
          {field.helpText}
        </p>
      )}
      {renderField(field)}
    </div>
  );
};

export default FormField; 